import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Surfaces an error from a promise-returning onClick the same way the browser
// would an uncaught one: a global `error` event (picked up by RUM and the
// console). Handlers are expected to handle their own errors (notifyError);
// this only keeps an unhandled one visible rather than letting the guard
// swallow it.
const reportUnhandled = (error) => {
  if (
    typeof window !== 'undefined' &&
    typeof window.reportError === 'function'
  ) {
    window.reportError(error);
    return;
  }
  // eslint-disable-next-line no-console
  console.error(error);
};

// TODO: Maybe replace disabled programmatic styles with disabled:cursor-not-allowed

const Button = React.forwardRef(
  (
    {
      'aria-label': ariaLabel,
      'data-testid': datatestid,
      className,
      htmlType,
      type,
      size,
      disabled,
      loading,
      onClick,
      onMouseEnter,
      onMouseLeave,
      icon,
      children,
      isRounded,
      isCentered,
      to,
      href,
    },
    forwardedRef,
  ) => {
    const isIconOnly = icon && !children;

    // In-flight guard: when `onClick` returns a promise the button shows the
    // spinner and ignores further clicks until that promise settles. This is
    // the application-wide default for any action that triggers slow
    // server-side work (TECH-3695 QA-3 Defect 1). Pass `loading` explicitly
    // when the pending state lives elsewhere (see utils/hooks/useInFlight).
    // A setState after unmount is a no-op in React 18, so no mounted guard.
    const [inFlight, setInFlight] = useState(false);

    const isLoading = loading || inFlight;

    const typeClassName = useMemo(() => {
      // A button that is working looks different from one that is disabled
      // (grey): blue wash and blue text (brand tint, not a clinical status
      // colour) plus a wait cursor, so the user can see where the work is
      // happening. The secondary variant keeps its border so the width does
      // not jump.
      const inProgressClassName =
        type === 'secondary'
          ? 'bg-primary-200 text-primary-800 border border-primary-800 cursor-wait'
          : 'bg-primary-200 text-primary-800 cursor-wait';

      switch (type) {
        case 'primary':
          if (disabled) {
            return 'bg-disabled text-on-disabled cursor-not-allowed';
          }

          if (isLoading) {
            return inProgressClassName;
          }

          return 'bg-primary-800 hover:bg-primary-700 text-on-primary-800';
        case 'secondary':
          if (disabled) {
            return 'bg-surface text-on-disabled border border-on-disabled cursor-not-allowed';
          }

          if (isLoading) {
            return inProgressClassName;
          }

          return 'bg-surface hover:bg-primary-300 text-primary-800 border border-primary-800';
        case 'tertiary':
          if (disabled) {
            return 'bg-transparent text-on-disabled cursor-not-allowed';
          }

          if (isLoading) {
            return inProgressClassName;
          }

          return 'bg-transparent hover:bg-neutral-500 dark:hover:bg-neutral-800';
        case 'ghost':
          if (disabled) {
            return 'bg-transparent text-on-disabled cursor-not-allowed';
          }

          if (isLoading) {
            return inProgressClassName;
          }

          return 'bg-transparent hover:bg-neutral-500 dark:hover:bg-neutral-800 text-primary-800';
        case 'danger':
          if (disabled) {
            return 'bg-disabled text-on-disabled cursor-not-allowed';
          }

          if (isLoading) {
            return inProgressClassName;
          }

          return 'bg-danger-500 hover:bg-danger-400 text-on-danger-500';
        default:
          return '';
      }
    }, [type, disabled, isLoading]);

    const sizeClassName = useMemo(() => {
      let str = '';

      switch (size) {
        case 'small':
          str = 'h-6 text-sm';

          if (isIconOnly) {
            str += ' w-6';
          }

          break;
        case 'medium':
          str = 'h-8 text-sm';

          if (isIconOnly) {
            str += ' w-8';
          }

          break;
        case 'large':
          str = 'h-10';

          if (isIconOnly) {
            str += ' w-10';
          }

          break;
        case 'xl':
          str = 'h-12 text-lg';

          if (isIconOnly) {
            str += ' w-12';
          }

          break;
        default:
          break;
      }

      return str;
    }, [size, isIconOnly]);

    const shapeClassName = useMemo(() => {
      if (isRounded) {
        return 'rounded-full';
      }

      return 'rounded-md';
    }, [isRounded]);

    const handleOnClick = (event) => {
      if (isLoading || disabled) {
        // Normally unreachable: a disabled <button> dispatches no click. Kept
        // for the render between a handler's promise and the DOM update.
        event?.preventDefault?.();
        return;
      }

      const result = onClick(event);

      if (result && typeof result.then === 'function') {
        setInFlight(true);
        // Errors stay the handler's responsibility (e.g. notifyError). The
        // guard only observes settlement; a rejection the handler left
        // unhandled is reported, not swallowed.
        result.then(
          () => setInFlight(false),
          (error) => {
            setInFlight(false);
            reportUnhandled(error);
          },
        );
      }
    };

    let Component = 'button';
    let componentProps = {
      onMouseEnter,
      onMouseLeave,
      onClick: handleOnClick,
      type: htmlType,
      // A working button is natively disabled: the browser then dispatches
      // no click at all, so nothing bubbles to a clickable row or dropdown
      // behind it, and the Cypress suite's `should('not.be.disabled')` waits
      // keep working as a sync point. aria-busy tells screen readers why.
      // review:accepted: a natively disabled button drops keyboard focus to body while
      // working; keeping it focusable (aria-disabled) broke Cypress sync points and let
      // clicks bubble, so the a11y follow-up is tracked in change-log/TECH-3695/TODO.md.
      disabled: disabled || isLoading,
      'aria-busy': isLoading ? true : undefined,
    };

    if (to) {
      Component = Link;
      componentProps = { to };
    } else if (href) {
      Component = 'a';
      componentProps = { href, target: '_blank', rel: 'noopener noreferrer' };
    }

    return (
      <Component
        ref={forwardedRef}
        aria-label={ariaLabel}
        data-testid={datatestid}
        className={`z-btn zedoc-input-group__item whitespace-nowrap ${shapeClassName} transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${className} ${typeClassName} ${sizeClassName} ${
          isLoading || icon ? 'cluster-2 items-center' : ''
        } ${isCentered ? 'justify-center' : ''} ${
          isIconOnly ? 'flex justify-center' : 'px-4'
        } ${
          to || href
            ? 'text-body hover:text-body focus:text-body hover:no-underline focus:no-underline'
            : ''
        }`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...componentProps}
      >
        {isLoading ? (
          // NOTE: The spinner icon is taken from https://v2.tailwindcss.com/docs/animation#spin example
          <svg
            className="animate-spin w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          icon
        )}
        {children}
      </Component>
    );
  },
);

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  'aria-label': PropTypes.string,
  'data-testid': PropTypes.string,
  className: PropTypes.string,
  htmlType: PropTypes.string,
  icon: PropTypes.node,
  type: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'ghost',
    'danger',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large', 'xl']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  isRounded: PropTypes.bool,
  isCentered: PropTypes.bool,
  to: PropTypes.string,
  href: PropTypes.string,
};

const noop = () => {};
Button.defaultProps = {
  children: null,
  'aria-label': null,
  'data-testid': null,
  className: '',
  htmlType: 'button',
  icon: null,
  type: 'secondary',
  size: 'large',
  disabled: false,
  loading: false,
  onClick: noop,
  onMouseEnter: noop,
  onMouseLeave: noop,
  isRounded: false,
  isCentered: true,
  to: null,
  href: null,
};

export default Button;
